import { combineEpics } from 'redux-observable';
import {
  concatMap,
  delay,
  filter,
  from,
  map,
  mergeMap,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';

import navigation from 'domains/navigation';
import cartModule from 'domains/cart';
import cartBilling from 'domains/cart_billing';
import cartProducts from 'domains/cart_products';
import session from 'domains/session';

import * as projectsAPI from './projects.api';

import projects from './index';

import { catchAndLogError } from 'utils/common';

import type { Epic } from 'redux-observable';
import type { Action } from '@reduxjs/toolkit';
import type { RootState } from 'store/wos_store';

const { actions } = projects;

/**
 * The first time we view the cart as a logged in user, fetch projects
 */
export const startGetProjectsEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    withLatestFrom(state$),
    map(([, state]) => state),
    filter((state) => session.selectors.isLoggedIn(state)),
    filter((state) => navigation.selectors.showCart(state)),
    take(1),
    map(() => actions.getAll.pending())
  );

export const getProjectsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.getAll.pending.match),
    switchMap(() =>
      from(projectsAPI.all()).pipe(
        map(actions.getAll.fulfilled),
        catchAndLogError(actions.getAll.rejected())
      )
    )
  );

/**
 * Creates a project, which turns the original cart into a source template,
 * then restores that project as a new version which becomes the user's cart
 */
export const createProjectEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.create.pending.match),
    mergeMap(({ payload }) =>
      from(projectsAPI.create(payload)).pipe(
        map(actions.create.fulfilled),
        catchAndLogError(actions.create.rejected())
      )
    )
  );

/**
 * Refresh the projects once a new one has been created
 */
export const refreshProjectsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.create.fulfilled.match),
    mergeMap(() => from(projectsAPI.all()).pipe(map(actions.addMany), catchAndLogError()))
  );

/**
 * Maps the createProject.fulfilled action to the restore action
 */
export const mapCreateToRestoreProjectEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.create.fulfilled.match),
    map(({ payload }) => actions.restore.pending(payload))
  );

/**
 * Update the name/description of a project
 */
export const updateProjectEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.update.pending.match),
    map(({ payload }) => {
      if (payload.description === '') {
        payload.description = undefined;
      }

      return payload;
    }),
    mergeMap((payload) =>
      from(projectsAPI.update(payload)).pipe(
        map(() => actions.update.fulfilled(payload)),
        catchAndLogError(actions.update.rejected())
      )
    )
  );

/**
 * Sets a new cart based on the source cart of a project
 */
export const restoreProjectEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.restore.pending.match),
    mergeMap(({ payload }) =>
      from(projectsAPI.restore(payload)).pipe(
        mergeMap(() => from([cartModule.actions.get.pending(), cartProducts.actions.reset()])),
        catchAndLogError(actions.restore.rejected())
      )
    )
  );

/**
 * When both a restore project action and a following get cart action come through,
 * refresh the cart products and billing details
 */
export const restoreProjectGetCartProductsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.restore.pending.match),
    mergeMap(() =>
      action$.pipe(
        filter(cartModule.actions.get.fulfilled.match),
        take(1),
        concatMap(() =>
          from([
            cartBilling.actions.get.pending(),
            cartProducts.actions.getAll.pending(),
            actions.restore.fulfilled(),
          ])
        )
      )
    )
  );

/**
 * After we restore, we usually will need to do some redirection/reloading
 * triggered by the change in restore status. So we just wait a beat, then reset
 * the status for the next time we restore
 */
export const resetRestoreStatusEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.restore.fulfilled.match),
    delay(500),
    map(() => actions.resetRestoreStatus())
  );

export const deleteProjectEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.destroy.pending.match),
    mergeMap(({ payload }) =>
      from(projectsAPI.destroy(payload.guid)).pipe(
        map(() => actions.destroy.fulfilled(payload.id)),
        catchAndLogError(actions.destroy.rejected())
      )
    )
  );

export default combineEpics(
  startGetProjectsEpic,
  getProjectsEpic,
  createProjectEpic,
  refreshProjectsEpic,
  mapCreateToRestoreProjectEpic,
  updateProjectEpic,
  restoreProjectEpic,
  restoreProjectGetCartProductsEpic,
  resetRestoreStatusEpic,
  deleteProjectEpic
);
